/**
 * useOperationLifecycle.js — RC 1.53 Phase 4.1
 *
 * React hook that subscribes a section to its action's operation
 * lifecycle. Phase 4.1 SKELETON ONLY — no section is wired to this
 * hook yet. The hook returns the controller's per-(job, action)
 * snapshot plus a thin `dispatch` / `abort` API.
 *
 * Phase 4.2 will migrate one section (CvSection) to consume this
 * hook. Until then, this module is dormant.
 *
 * Usage (post-4.2, illustrative):
 *
 *   const {
 *     status, runId, isAnyRunning,
 *     dispatch, abort, terminalEnvelope, error,
 *   } = useOperationLifecycle(jobId, 'cv_variant');
 *
 *   await dispatch({ body: {...}, options: {...} });
 */

import { useCallback, useEffect, useState } from 'react';
import {
  dispatch as controllerDispatch,
  abort as controllerAbort,
  attachExistingRun,
  getOperation,
  isAnyRunning as controllerIsAnyRunning,
  markComplete as controllerMarkComplete,
  markFailed as controllerMarkFailed,
  OPERATION_STATUS,
} from './controller.js';
import { subscribe, OPERATION_EVENT_TYPES } from './operationEvents.js';

const _ALLOWED_ACTIONS = new Set([
  'cv_variant', 'cover_letter', 'interview_prep', 'analyse_fit',
]);

export function useOperationLifecycle(jobId, actionCode) {
  if (actionCode && !_ALLOWED_ACTIONS.has(actionCode)) {
    throw new Error(
      `useOperationLifecycle: unknown actionCode '${actionCode}'`,
    );
  }

  const [snapshot, setSnapshot] = useState(() =>
    (jobId && actionCode) ? getOperation(jobId, actionCode) : null,
  );
  const [anyRunning, setAnyRunning] = useState(() =>
    jobId ? controllerIsAnyRunning(jobId) : false,
  );

  useEffect(() => {
    if (!jobId || !actionCode) return undefined;

    const refresh = () => {
      setSnapshot(getOperation(jobId, actionCode));
      setAnyRunning(controllerIsAnyRunning(jobId));
    };

    // Subscribe to every lifecycle event and re-read the snapshot.
    // Cheap: the controller's state is in-memory, and we filter on
    // (jobId, actionCode) so unrelated events do not trigger renders.
    const unsubs = [
      OPERATION_EVENT_TYPES.START,
      OPERATION_EVENT_TYPES.PROGRESS,
      OPERATION_EVENT_TYPES.STABILIZING,
      OPERATION_EVENT_TYPES.COMPLETE,
      OPERATION_EVENT_TYPES.FAIL,
      OPERATION_EVENT_TYPES.ABORT,
    ].map((type) =>
      subscribe(type, (detail) => {
        // Refresh `anyRunning` for any event in this job; only
        // refresh `snapshot` for events matching this action.
        if (detail.jobId !== jobId) return;
        if (detail.actionCode === actionCode) refresh();
        else setAnyRunning(controllerIsAnyRunning(jobId));
      }),
    );
    // Initial sync — section may mount after a dispatch fired.
    refresh();
    return () => { unsubs.forEach((u) => u && u()); };
  }, [jobId, actionCode]);

  const dispatch = useCallback(
    (args) => controllerDispatch({ jobId, actionCode, ...args }),
    [jobId, actionCode],
  );
  const abort = useCallback(
    (reason) => controllerAbort(jobId, actionCode, reason),
    [jobId, actionCode],
  );
  const attach = useCallback(
    (args) => attachExistingRun({ jobId, actionCode, ...args }),
    [jobId, actionCode],
  );
  const markComplete = useCallback(
    (envelope) => controllerMarkComplete(jobId, actionCode, envelope),
    [jobId, actionCode],
  );
  const markFailed = useCallback(
    (err) => controllerMarkFailed(jobId, actionCode, err),
    [jobId, actionCode],
  );

  return {
    status:           snapshot?.status || OPERATION_STATUS.IDLE,
    runId:            snapshot?.runId || null,
    terminalEnvelope: snapshot?.terminalEnvelope || null,
    error:            snapshot?.error || null,
    idempotencyKey:   snapshot?.idempotencyKey || null,
    isAnyRunning:     anyRunning,
    dispatch,
    abort,
    attach,
    markComplete,
    markFailed,
  };
}
